// Kullanıcı Yönetimi — plan §4.7.1 + design ref #screen-users-list.
// Metric kartları + FilteredTable (search + rol + aktif filtresi) + son giriş kolonu.
// Davet akışı /admin/users/new sayfasına taşındı.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { roleDisplayName, type UsersListResponse, type UserListItem } from "@mahir/shared";
import { api } from "../../api/client";
import { SUPERADMIN_ONLY_HINT, assignableRoles, canChangeUserRole } from "../../lib/role-ui";
import { useAuthStore } from "../../stores/auth-store";
import {
  FilteredTable,
  useDebouncedValue,
  type Column,
} from "../../components/common/FilteredTable";

const PAGE_SIZE = 25;

export function UsersList(): JSX.Element {
  const currentUser = useAuthStore((s) => s.user);
  const currentUserId = currentUser?.id;
  const navigate = useNavigate();
  const [data, setData] = useState<UsersListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  // Filtreler — client-side (toplam kullanıcı sayısı azdır; BE pagination M8 için)
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 250);
  const [roleId, setRoleId] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, roleId, activeFilter]);

  // BE pagination + filter — M8-S1
  useEffect(() => {
    let aborted = false;
    setLoading(true);
    const p = new URLSearchParams();
    p.set("page", page.toString());
    p.set("limit", PAGE_SIZE.toString());
    if (debouncedSearch.trim()) p.set("search", debouncedSearch.trim());
    if (roleId) p.set("roleId", roleId);
    if (activeFilter === "active") p.set("isActive", "true");
    if (activeFilter === "inactive") p.set("isActive", "false");
    void api<UsersListResponse>(`/admin/users?${p.toString()}`)
      .then((res) => {
        if (!aborted) {
          setData(res);
          setError(null);
        }
      })
      .catch((err) => {
        if (!aborted) setError(err instanceof Error ? err.message : "Liste yüklenemedi");
      })
      .finally(() => {
        if (!aborted) setLoading(false);
      });
    return () => {
      aborted = true;
    };
  }, [refreshTick, page, debouncedSearch, roleId, activeFilter]);

  const pageItems = data?.users ?? [];

  const metrics = useMemo(() => {
    if (!data) return { total: 0, active: 0, consultant: 0, writer: 0 };
    return {
      total: data.users.length,
      active: data.users.filter((u) => u.isActive).length,
      consultant: data.users.filter((u) => u.roleName.toLowerCase().includes("danışman")).length,
      writer: data.users.filter((u) => u.roleName.toLowerCase().includes("yazar")).length,
    };
  }, [data]);

  async function onToggleActive(u: UserListItem) {
    if (u.id === currentUserId) {
      alert("Kendi hesabınızı deaktive edemezsiniz.");
      return;
    }
    try {
      if (u.isActive) {
        await api(`/admin/users/${u.id}`, { method: "DELETE" });
      } else {
        await api(`/admin/users/${u.id}`, {
          method: "PATCH",
          body: { isActive: true },
        });
      }
      setRefreshTick((t) => t + 1);
    } catch (err) {
      alert(err instanceof Error ? err.message : "Güncellenemedi");
    }
  }

  async function onChangeRole(u: UserListItem, newRoleId: string) {
    if (newRoleId === u.roleId) return;
    try {
      await api(`/admin/users/${u.id}`, { method: "PATCH", body: { roleId: newRoleId } });
      setRefreshTick((t) => t + 1);
    } catch (err) {
      alert(err instanceof Error ? err.message : "Rol değiştirilemedi");
    }
  }

  async function onTriggerReset(u: UserListItem) {
    if (u.id === currentUserId) {
      alert("Kendi hesabınıza reset tetiklemek için Forgot Password akışını kullanın.");
      return;
    }
    const confirmed = window.confirm(
      `${u.fullName} (${u.email}) için şifre sıfırlama maili gönderilecek.\n\nMevcut şifre geçersiz olacak. Devam?`,
    );
    if (!confirmed) return;
    try {
      await api(`/admin/users/${u.id}/trigger-reset`, { method: "POST", body: {} });
      alert("Şifre sıfırlama maili gönderildi.");
    } catch (err) {
      alert(err instanceof Error ? err.message : "Reset tetiklenemedi");
    }
  }

  const columns: Column<UserListItem>[] = [
    {
      key: "name",
      label: "Ad Soyad",
      render: (u) => (
        <div>
          <div style={{ fontWeight: 500 }}>
            {u.fullName}
            {u.id === currentUserId ? (
              <span
                style={{
                  marginLeft: 6,
                  fontSize: "0.65rem",
                  color: "var(--color-accent)",
                  fontWeight: 700,
                }}
              >
                (SİZ)
              </span>
            ) : null}
            {u.userKind === "client" ? (
              <span
                style={{
                  marginLeft: 6,
                  fontSize: "0.65rem",
                  padding: "1px 7px",
                  borderRadius: 999,
                  background: "color-mix(in srgb, var(--color-primary) 12%, white)",
                  color: "var(--color-primary)",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
                title="Müşteri katılımcı — rolü davet akışı dışında değiştirilemez"
              >
                Müşteri
              </span>
            ) : null}
          </div>
          <div
            style={{
              fontSize: "0.72rem",
              color: "var(--color-text-3)",
              fontFamily: "var(--font-mono)",
            }}
          >
            {u.email}
          </div>
        </div>
      ),
    },
    {
      key: "role",
      label: "Rol",
      render: (u) => {
        // D14 (Müşteri Katılımcı Rolü, eng-review) — 'musteri' sistem rolü davet
        // akışı dışında atanamaz: internal kullanıcılara seçenek olarak gösterilmez;
        // client kullanıcının kendi 'musteri' rolü (mevcut değer) görünür kalır ama
        // dropdown tamamen disabled — rolü bu ekrandan asla değiştirilemez.
        const isClient = u.userKind === "client";
        // Finar rev. madde 12: Sistem Yöneticisi rolünü yalnız Sistem Yöneticisi atar/kaldırır.
        const locked = !canChangeUserRole(u.roleName, currentUser);
        const options = assignableRoles(data?.roles ?? [], currentUser, u.roleId).filter(
          (r) => r.name !== "musteri" || r.id === u.roleId,
        );
        return (
          <select
            value={u.roleId}
            onChange={(e) => void onChangeRole(u, e.target.value)}
            onClick={(e) => e.stopPropagation()}
            disabled={u.id === currentUserId || isClient || locked}
            title={
              isClient
                ? "Müşteri kullanıcının rolü admin ekranından değiştirilemez"
                : locked
                  ? SUPERADMIN_ONLY_HINT
                  : undefined
            }
            style={{
              padding: "3px 6px",
              borderRadius: 4,
              border: "1px solid var(--color-border)",
              background: "var(--color-surface)",
              fontSize: "0.8rem",
            }}
          >
            {options.map((r) => (
              <option key={r.id} value={r.id}>
                {roleDisplayName(r.name)}
              </option>
            ))}
          </select>
        );
      },
    },
    {
      key: "lastLogin",
      label: "Son Giriş",
      render: (u) =>
        u.lastLoginAt ? (
          <span style={{ fontSize: "0.82rem", color: "var(--color-text-2)" }}>
            {new Date(u.lastLoginAt).toLocaleString("tr-TR", {
              day: "2-digit",
              month: "short",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
        ) : (
          <span style={{ color: "var(--color-text-3)", fontSize: "0.82rem" }}>—</span>
        ),
    },
    {
      key: "status",
      label: "Durum",
      render: (u) => (
        <span
          style={{
            padding: "2px 10px",
            borderRadius: 999,
            background: u.isActive
              ? "color-mix(in srgb, var(--color-success) 12%, white)"
              : "rgba(198,40,40,0.08)",
            color: u.isActive ? "var(--color-success)" : "var(--color-error)",
            fontSize: "0.72rem",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
          }}
        >
          {u.isActive ? "Aktif" : "Deaktif"}
        </span>
      ),
    },
    {
      key: "actions",
      label: "",
      align: "right",
      render: (u) =>
        // Sistem Yöneticisi'ni (reset/pasifleştirme) yalnız Sistem Yöneticisi yönetir.
        u.id === currentUserId || !canChangeUserRole(u.roleName, currentUser) ? (
          <span
            style={{ color: "var(--color-text-3)", fontSize: "0.78rem" }}
            title={u.id === currentUserId ? undefined : SUPERADMIN_ONLY_HINT}
          >
            —
          </span>
        ) : (
          <div
            style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => void onTriggerReset(u)}
              className="px-2.5 py-1 text-xs rounded"
              style={{
                background: "transparent",
                border: "1px solid var(--color-border)",
                color: "var(--color-text-2)",
              }}
              title="Şifre sıfırlama maili gönder"
            >
              🔑 Reset
            </button>
            <button
              onClick={() => void onToggleActive(u)}
              className="px-2.5 py-1 text-xs rounded"
              style={{
                background: "transparent",
                border: "1px solid var(--color-border)",
                color: u.isActive ? "var(--color-error)" : "var(--color-primary)",
              }}
            >
              {u.isActive ? "Deaktive" : "Aktive"}
            </button>
          </div>
        ),
    },
  ];

  return (
    <div className="p-8" style={{ fontFamily: "var(--font-sans)" }}>
      <div className="mb-6 max-w-7xl">
        <div
          style={{
            fontSize: "0.72rem",
            color: "var(--color-primary)",
            textTransform: "uppercase",
            letterSpacing: "0.12em",
            fontWeight: 600,
            marginBottom: 6,
          }}
        >
          KULLANICILAR
        </div>
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1
              className="text-3xl"
              style={{ fontFamily: "var(--font-display)", color: "var(--color-text)" }}
            >
              Kullanıcı Yönetimi
            </h1>
            <p className="text-sm mt-1" style={{ color: "var(--color-text-2)" }}>
              Ekibinizi ve rol atamalarını yönetin.
            </p>
          </div>
          <button
            onClick={() => void navigate("/admin/users/new")}
            className="px-4 py-2 rounded-lg font-semibold text-sm"
            style={{ background: "var(--color-primary)", color: "var(--color-surface)" }}
          >
            + Davet Et
          </button>
        </div>
      </div>

      {/* Metrik kartları */}
      <div className="grid grid-cols-4 gap-3 mb-6 max-w-7xl">
        <Metric label="Toplam" value={metrics.total} />
        <Metric label="Aktif" value={metrics.active} tone="success" />
        <Metric label="Danışman" value={metrics.consultant} tone="primary" />
        <Metric label="Yazar" value={metrics.writer} tone="muted" />
      </div>

      {/* M9-S2: error artık FilteredTable errorMessage prop'uyla render — banner kaldırıldı */}

      <div className="max-w-7xl">
        <FilteredTable
          items={pageItems}
          columns={columns}
          rowKey={(u) => u.id}
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Ad veya email ara…"
          filters={[
            {
              key: "role",
              label: "Rol (tümü)",
              value: roleId,
              onChange: setRoleId,
              options: (data?.roles ?? []).map((r) => ({
                value: r.id,
                label: roleDisplayName(r.name),
              })),
            },
            {
              key: "active",
              label: "Durum (tümü)",
              value: activeFilter,
              onChange: setActiveFilter,
              options: [
                { value: "active", label: "Aktif" },
                { value: "inactive", label: "Deaktif" },
              ],
            },
          ]}
          total={data?.total ?? 0}
          page={page}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
          loading={loading}
          emptyMessage={
            debouncedSearch || roleId || activeFilter
              ? "Filtrelerinize uygun kullanıcı bulunamadı."
              : "Henüz kullanıcı yok"
          }
          emptyHint={
            debouncedSearch || roleId || activeFilter
              ? "Filtreleri temizleyin."
              : "Üstteki 'Kullanıcı davet et' butonuyla başlayın."
          }
          errorMessage={error}
          onRetry={() => setRefreshTick((t) => t + 1)}
        />
      </div>
    </div>
  );
}

function Metric({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: number;
  tone?: "default" | "muted" | "primary" | "success";
}): JSX.Element {
  const color =
    tone === "primary"
      ? "var(--color-primary)"
      : tone === "success"
        ? "var(--color-success)"
        : tone === "muted"
          ? "var(--color-text-3)"
          : "var(--color-text)";
  return (
    <div
      className="rounded-xl p-4"
      style={{
        background: "var(--color-surface)",
        border: "1px solid var(--color-border)",
      }}
    >
      <div
        style={{
          fontSize: "0.72rem",
          color: "var(--color-text-3)",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: "1.6rem",
          fontWeight: 600,
          fontFamily: "var(--font-display)",
          color,
          marginTop: 4,
        }}
      >
        {value}
      </div>
    </div>
  );
}
